/**
 * Reusable drop target representing one axis slot (X / Y / X2 / extra Y).
 *
 * Listens for HTML5 drops from the FieldPickerTree (dataTransfer key
 * `field`), parses the BuilderFieldDef, and bubbles the change up via
 * `onChange`. Renders the field name with a clear button when filled.
 */

import { useState, type DragEvent } from 'react';
import { X } from 'lucide-react';
import { type BuilderFieldDef, type FieldKind } from '../../../lib/api/reporting';
import { FIELD_DRAG_TYPE, FIELD_KIND_DRAG_PREFIX, decodeFieldDrag } from './FieldPickerTree';

interface AxisDropzoneProps {
  label: string;
  axis: 'x' | 'y' | 'x2' | 'y_extra';
  value: BuilderFieldDef | null;
  onChange: (next: BuilderFieldDef) => void;
  onClear: () => void;
  /** Optional helper text rendered below the dropzone. */
  helper?: string;
  /** If set, only fields whose `kind` is in this list are accepted. */
  acceptedKinds?: readonly FieldKind[];
  /** Called when a drop is rejected for failing the acceptedKinds check. */
  onReject?: (field: BuilderFieldDef, accepted: readonly FieldKind[]) => void;
}

function dragCarriesAcceptableKind(
  types: readonly string[],
  accepted: readonly FieldKind[] | undefined,
): boolean {
  if (!accepted) return true;
  return accepted.some((k) => types.includes(`${FIELD_KIND_DRAG_PREFIX}${k}`));
}

export function AxisDropzone({ label, axis, value, onChange, onClear, helper, acceptedKinds, onReject }: AxisDropzoneProps) {
  const [rejected, setRejected] = useState(false);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes(FIELD_DRAG_TYPE)) return;
    const ok = dragCarriesAcceptableKind(e.dataTransfer.types, acceptedKinds);
    e.preventDefault();
    e.dataTransfer.dropEffect = ok ? 'copy' : 'none';
    if (!ok && !rejected) setRejected(true);
    if (ok && rejected) setRejected(false);
  };

  const handleDragLeave = () => {
    if (rejected) setRejected(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setRejected(false);
    const raw = e.dataTransfer.getData(FIELD_DRAG_TYPE);
    const field = decodeFieldDrag(raw);
    if (!field) return;
    if (acceptedKinds && !acceptedKinds.includes(field.kind)) {
      onReject?.(field, acceptedKinds);
      return;
    }
    onChange(field);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        <span className="font-mono normal-case opacity-60">{axis}</span>
      </div>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`min-h-[44px] px-2 py-1.5 rounded border-2 border-dashed flex items-center justify-between gap-2 transition-colors ${
          rejected
            ? 'border-destructive bg-destructive/10 text-destructive'
            : value
              ? 'border-primary/40 bg-primary/5'
              : 'border-border bg-muted/30 text-muted-foreground'
        }`}
        aria-label={`${label} axis drop target`}
      >
        {value ? (
          <>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium truncate">{value.label}</div>
              <div className="text-[10px] text-muted-foreground truncate">
                DSID {value.dsid} · F{value.field_id}
              </div>
            </div>
            <button
              type="button"
              onClick={onClear}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              aria-label={`Clear ${label} axis`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <span className="text-xs">Drop a field here</span>
        )}
      </div>
      {helper && <div className="text-[10px] text-muted-foreground">{helper}</div>}
    </div>
  );
}
